import {
  Button,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  DialogActions,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import {
  DealerLocation,
  SetupType,
} from "../../models/features/Integration/DmsSetup";
import {
  useAddSetupLocationMutation,
  useGetDealerLocationsMutation,
  useGetDmsSetupByIdMutation,
  useGetStoreLocationsMutation,
  useSetDMSLocationDefaultMutation,
  useUpdateActualSetupLocationMutation,
  useUpdateSetupLocationMutation,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import TruncatedCellWithTooltip from "../common/TruncatedCellWithTooltip";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../redux/reducer/rootReducer";
import { Dispatch } from "@reduxjs/toolkit";
import EditLocationDialog from "./EditLocationDialog";
import {
  addDmsLocation,
  updateDmsLocation,
} from "../../redux/reducer/features/Auth/authSlice";
import { StyledTableCell } from "../common/StyledTableCell";
import { setupTypeLabelMap } from "../../services/utilities/utility";

export const LocationsSettings: React.FunctionComponent = () => {
  const dispatch = useDispatch<Dispatch<any>>();
  const { everestDmsDealerId, dmsDealerId, dmsSetup } = useSelector(
    (state: AppState) => state.authReducer
  );
  const [dmsLocations, setDmsLocations] = React.useState<DealerLocation[]>([]);
  const [selectedLocations, setSelectedLocations] = React.useState<
    DealerLocation[]
  >([]);
  const [nonAddedLocations, setNonAddedLocations] = React.useState<
    DealerLocation[]
  >([]);
  const [newAddedLocation, setNewAddedLocation] =
    React.useState<DealerLocation>();
  const [openLocation, setOpenLocation] = React.useState(false);
  const [storeLocations, setStoreLocations] = React.useState<any[]>([]);
  const [editLocation, setEditLocation] = React.useState<DealerLocation>();
  const [openActualEditLocation, setOpenActualEditLocation] =
    React.useState(false);
  const [openEditLocation, setOpenEditLocation] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState<any>(null);
  const [removeMappingConfirm, setRemoveMappingConfirm] = React.useState(false);
  const [selectedStoreLocation, setSelectedStoreLocation] = React.useState<any>(
    {}
  );
  const [errorMessage, setErrorMessage] = useState("");

  const [dmsSetupMutation, dmsSetupMutationResult] =
    useGetDmsSetupByIdMutation();
  const [storeLocationMutation, storeLocationMutationResult] =
    useGetStoreLocationsMutation();
  const [setLocationDefaultMutation, setLocationDefaultMutationResult] =
    useSetDMSLocationDefaultMutation();
  const [dmsLocationMutation, dmsLocationMutationResult] =
    useGetDealerLocationsMutation();
  const [addSetupLocationMutation, addSetupLocationMutationResult] =
    useAddSetupLocationMutation();
  const [updateActualLocationMutation, updateActualLocationMutationResult] =
    useUpdateActualSetupLocationMutation();
  const [updateLocationMutation, updateLocationMutationResult] =
    useUpdateSetupLocationMutation();

  React.useEffect(() => {
    dmsLocationMutation({
      everestDmsDealerId: everestDmsDealerId ?? "",
    }).then((response: any) => {
      if (response) {
        setDmsLocations(response?.data?.entityList);
      }
    });
  }, []);

  React.useEffect(() => {
    dmsSetupMutation({}).then((response: any) => {
      if (response && response.data && response.data.status === 0) {
        let data = response?.data.entity;
        handleGetStoreLocations(data.storeUrl, data.storeAccessToken);
        let selectedLocs: DealerLocation[] = data.dmsLocations.map(
          (loc: any) => ({
            id: loc.id,
            dealerId: loc.dealerId,
            dmsType: 0,
            dmsLocationId: loc.dmsLocationId,
            name: loc.name,
            address: loc.address,
            city: loc.city,
            stateOrProvince: loc.stateOrProvince,
            zipPostal: loc.zipPostal,
            storeLocationId: loc.storeLocationId,
            email: loc.email,
            phone: loc.phone,
            isDefault: loc.isDefault,
          })
        );
        setSelectedLocations(selectedLocs);
      }
    });
  }, []);

  const handleGetStoreLocations = (url: string, token: string) => {
    storeLocationMutation({
      accessToken: token,
      storeUrl: url,
      setupType: dmsSetup?.setupType ?? SetupType.Shopify,
    }).then((res: any) => {
      if (res.data && res.data.status === 200) {
        setStoreLocations(
          res?.data?.entityList?.map((loc: any) => ({
            id: loc.id,
            name: loc.name,
            address: loc.address1,
            code: loc.code,
          }))
        );
      }
    });
  };
  const handleOpenAddLocation = () => {
    setNewAddedLocation(undefined);
    setNonAddedLocations(
      dmsLocations.filter(
        (loc) =>
          !selectedLocations.some((x) => x.dmsLocationId === loc.dmsLocationId)
      )
    );
    setOpenLocation(true);
  };

  const getStoreLocationName = (storeLocationId: number) => {
    return storeLocations.find((loc) => loc.id === storeLocationId)?.name ?? "";
  };

  const handleSetDefault = (id: any, value: boolean) => {
    setSelectedLocations(
      selectedLocations.map((item) =>
        item.id === id
          ? { ...item, isDefault: value }
          : { ...item, isDefault: false }
      )
    );
    setLocationDefaultMutation({ locationId: id, isDefault: value });
  };

  const handleClose = () => {
    setErrorMessage("");
    setRemoveMappingConfirm(false);
    setOpenLocation(false);
    setOpenActualEditLocation(false);
    setOpenEditLocation(false);
    setSelectedStoreLocation(undefined);
  };

  const handleChangeDMSLocation = (value: any) => {
    var newLoc = dmsLocations.find((c) => c.dmsLocationId === value);
    setNewAddedLocation(newLoc);
  };

  const isMapped = (storeLocationId: number) => {
    return selectedLocations.some(
      (loc) => loc.storeLocationId === storeLocationId
    );
  };

  const handleChangeStoreLocation = (value: number) => {
    var newLoc = storeLocations.find((c) => c?.id === value);
    setSelectedStoreLocation(newLoc);
  };

  const handleSaveLocation = () => {
    if (newAddedLocation !== undefined) {
      addSetupLocationMutation({
        location: {
          ...newAddedLocation,
          storeLocationId: selectedStoreLocation.id ?? 0,
        },
      }).then((res: any) => {
        if (res && res.data) {
          if (res.data.status === 200) {
            dispatch(addDmsLocation(res.data.entity));
            setSelectedLocations([
              ...selectedLocations,
              {
                ...newAddedLocation,
                storeLocationId: selectedStoreLocation.id,
                id: res.data.entity.id,
              },
            ]);
            handleClose();
          } else {
            setErrorMessage(res?.data?.message);
          }
        }
      });
    }
  };

  const handleUpdateLocation = (location: DealerLocation) => {
    if (location !== undefined) {
      updateActualLocationMutation({
        location: location,
      }).then((res: any) => {
        if (res && res.data) {
          if (res.data.status === 200) {
            dispatch(addDmsLocation(res.data.entity));
            setSelectedLocations(
              selectedLocations.map((x) =>
                x.dealerId === res.data.entity.dealerId ? res.data.entity : x
              )
            );
            handleClose();
          } else {
            setErrorMessage(res?.data?.message);
          }
        }
      });
    }
  };

  const handleUpdateMapping = (storeLocationId: number) => {
    updateLocationMutation({
      locationId: selectedRow?.id,
      storeLocationId: storeLocationId,
      dealerId: selectedRow?.dealerId,
      dmsdealerId: dmsDealerId ?? "",
    }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 200) {
          dispatch(updateDmsLocation(res.data.entity));
          setRemoveMappingConfirm(false);
          setOpenEditLocation(false);
          setSelectedLocations(
            selectedLocations.map((loc) =>
              loc.id === selectedRow?.id
                ? { ...loc, storeLocationId: storeLocationId }
                : loc
            )
          );
        }
      }
    });
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={"center"}
        sx={{ pb: 2, mt: 4 }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Locations
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={handleOpenAddLocation}
          startIcon={<AddIcon />}
        >
          Add Location
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ fontWeight: "bold" }}>
                Dealer Id
              </StyledTableCell>
              <StyledTableCell sx={{ fontWeight: "bold" }}>
                DMS Location Name
              </StyledTableCell>
              <StyledTableCell sx={{ fontWeight: "bold" }}>
                {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}{" "}
                Location Name
              </StyledTableCell>
              <StyledTableCell sx={{ fontWeight: "bold" }}>
                Address
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ fontWeight: "bold" }}>
                Zip Code
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ fontWeight: "bold" }}>
                Default
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ fontWeight: "bold" }}>
                Status
              </StyledTableCell>
              <StyledTableCell sx={{ fontWeight: "bold" }} align="center">
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedLocations.length > 0 ? (
              selectedLocations.map((row) => (
                <TableRow key={row.dmsLocationId}>
                  <StyledTableCell sx={{ padding: 1.5 }}>
                    {row.dealerId}
                  </StyledTableCell>
                  <StyledTableCell sx={{ padding: 1.5 }}>
                    {<TruncatedCellWithTooltip value={row.name} />}
                  </StyledTableCell>
                  <StyledTableCell sx={{ padding: 1.5 }}>
                    {
                      <TruncatedCellWithTooltip
                        value={getStoreLocationName(row.storeLocationId)}
                      />
                    }
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      padding: 1.5,
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    align="left"
                  >
                    {<TruncatedCellWithTooltip value={row.address} />}
                  </StyledTableCell>
                  <StyledTableCell sx={{ padding: 1.5 }} align="center">
                    {<TruncatedCellWithTooltip value={row.zipPostal} />}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {" "}
                    <Switch
                      checked={row?.isDefault ?? false}
                      name="isDefault"
                      size="small"
                      onChange={(e) => {
                        handleSetDefault(row.id, e.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </StyledTableCell>
                  <StyledTableCell sx={{ padding: 1.5 }} align="center">
                    {row.storeLocationId > 0 ? (
                      <Chip size="small" color="success" label="Mapped"></Chip>
                    ) : (
                      <Chip
                        size="small"
                        color="error"
                        label="Not Mapped"
                      ></Chip>
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={{ padding: 0.5 }} align="center">
                    <IconButton
                      id="basic-button"
                      onClick={(n) => {
                        setSelectedRow(row);
                        setOpenEditLocation(true);
                      }}
                    >
                      <LocationSearchingIcon fontSize="small" />
                    </IconButton>{" "}
                    |{" "}
                    <IconButton
                      id="basic-button"
                      onClick={(n) => {
                        setEditLocation(row);
                        setOpenActualEditLocation(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>{" "}
                    |{" "}
                    <IconButton
                      disabled={row.storeLocationId === 0}
                      id="basic-button"
                      onClick={(n) => {
                        setSelectedRow(row);
                        setRemoveMappingConfirm(true);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell align="center" colSpan={7}>
                  No Rows to Show
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={removeMappingConfirm} onClose={handleClose} fullWidth>
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Confirm to Remove Mapping
        </DialogTitle>
        <DialogContent>
          Are you sure to remove{" "}
          {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]} location
          mapping with this DMS Location?
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            autoFocus
            onClick={() => {
              setRemoveMappingConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            disabled={updateLocationMutationResult.isLoading}
            endIcon={
              updateLocationMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <></>
              )
            }
            onClick={() => {
              handleUpdateMapping(0);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openLocation} onClose={handleClose} fullWidth>
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Add Location
        </DialogTitle>
        <DialogContent sx={{ minHeight: "15vh" }}>
          <Typography fontSize="15px">
            Select a DMS location and a{" "}
            {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}{" "}
            location to map them.
          </Typography>
          <Box>
            <FormControl variant="outlined" fullWidth sx={{ mt: 3 }}>
              <InputLabel id="location">DMS Location</InputLabel>
              <Select
                labelId="location"
                id="locationId"
                size="small"
                label="DMS Location"
                fullWidth
                value={newAddedLocation?.dmsLocationId ?? 0}
                onChange={(e) => handleChangeDMSLocation(e.target.value)}
              >
                {nonAddedLocations.map((col) => (
                  <MenuItem key={col.dmsLocationId} value={col.dmsLocationId}>
                    <ListItemText primary={col.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControl variant="outlined" fullWidth sx={{ mt: 3 }}>
            <InputLabel id="store-location">
              {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}{" "}
              location
            </InputLabel>
            <Select
              labelId="store-location"
              id="storelocationId"
              size="small"
              label={`${
                setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]
              } Location`}
              fullWidth
              value={selectedStoreLocation?.id ?? 0}
              onChange={(e) => handleChangeStoreLocation(e.target.value)}
            >
              {storeLocations.map((col) => (
                <MenuItem
                  disabled={isMapped(col.id)}
                  key={col.id}
                  value={col.id}
                >
                  <ListItemText primary={col.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography mt={3} color="error">
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              nonAddedLocations.length === 0 ||
              !newAddedLocation ||
              addSetupLocationMutationResult.isLoading ||
              !selectedStoreLocation
            }
            endIcon={
              addSetupLocationMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <></>
              )
            }
            onClick={() => {
              if (newAddedLocation) handleSaveLocation();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <EditLocationDialog
        handleClose={handleClose}
        handleUpdateLocation={handleUpdateLocation}
        editLocation={editLocation}
        openActualEditLocation={openActualEditLocation}
        isLoading={updateActualLocationMutationResult.isLoading}
      ></EditLocationDialog>

      <Dialog open={openEditLocation} onClose={handleClose} fullWidth>
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Edit Mapping
        </DialogTitle>
        <DialogContent sx={{ minHeight: "15vh" }}>
          <Typography fontSize="15px">
            Select a{" "}
            {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}{" "}
            location to complete mapping.
          </Typography>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel id="shopify-location">
              {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}{" "}
              location
            </InputLabel>
            <Select
              labelId="shopify-location"
              id="shopifylocationId"
              size="small"
              label={`${
                setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]
              } Location`}
              fullWidth
              value={selectedStoreLocation?.id ?? 0}
              onChange={(e) => handleChangeStoreLocation(e.target.value)}
            >
              {storeLocations.map((col) => (
                <MenuItem
                  disabled={isMapped(col.id)}
                  key={col.id}
                  value={col.id}
                >
                  <ListItemText primary={col.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography mt={3} color="error">
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              updateLocationMutationResult.isLoading || !selectedStoreLocation
            }
            endIcon={
              updateLocationMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <></>
              )
            }
            onClick={() => {
              if (selectedRow?.id && selectedStoreLocation)
                handleUpdateMapping(selectedStoreLocation.id);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
